import React, { useState, useMemo, useEffect } from 'react';
import { ISuggestion } from './types';
import { useViewport } from '../hooks/useViewport';

interface PositionedSuggestionsProps {
  suggestions: ISuggestion[];
  blockElement: HTMLElement | null;
  onAccept: (suggestion: ISuggestion) => void;
  onReject: (suggestion: ISuggestion) => void;
}

const PositionedSuggestions: React.FC<PositionedSuggestionsProps> = ({
  suggestions,
  blockElement,
  onAccept,
  onReject
}) => {
  const { isMobile } = useViewport();
  const [hoveredSuggestion, setHoveredSuggestion] = useState<string | null>(null);
  const [positions, setPositions] = useState<Map<string, number>>(new Map());
  
  // Styles inline modifiés pour affichage à gauche
  const styles = {
    container: {
      position: 'absolute' as const,
      right: '100%',  // Changé de left: '100%' à right: '100%'
      top: 0,
      width: '320px',
      marginRight: '16px',  // Changé de marginLeft à marginRight
      zIndex: 10
    },
    mobileContainer: {
      marginTop: '16px'
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '8px',
      border: '1px solid #e5e7eb',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
      transition: 'all 0.2s ease',
      cursor: 'pointer'
    },
    cardHovered: {
      borderColor: '#3b82f6',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
    },
    selectedText: {
      fontSize: '11px',
      color: '#6b7280',
      fontStyle: 'italic',
      marginBottom: '4px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap' as const
    },
    date: {
      fontSize: '12px',
      color: '#6b7280',
      marginBottom: '8px'
    },
    content: {
      fontSize: '14px',
      marginBottom: '12px',
      lineHeight: '1.4'
    },
    buttons: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '8px'
    },
    acceptButton: {
      padding: '4px 12px',
      backgroundColor: '#10b981',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      fontSize: '14px',
      cursor: 'pointer',
      transition: 'background-color 0.2s'
    },
    rejectButton: {
      padding: '4px 12px',
      backgroundColor: '#ef4444',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      fontSize: '14px',
      cursor: 'pointer',
      transition: 'background-color 0.2s'
    }
  };
  
  // Calculer les positions pour chaque suggestion
  useEffect(() => {
    if (!blockElement || isMobile) return;
    
    const calculatePositions = () => {
      const newPositions = new Map<string, number>();
      
      suggestions.forEach(suggestion => {
        if (suggestion.position?.selectedText) {
          const textContent = blockElement.textContent || '';
          const index = textContent.indexOf(suggestion.position.selectedText);
          
          if (index !== -1) {
            // Estimation approximative de la position verticale
            const approximateY = (index / textContent.length) * blockElement.offsetHeight;
            newPositions.set(suggestion._id, approximateY);
          } else {
            newPositions.set(suggestion._id, 0);
          }
        } else {
          newPositions.set(suggestion._id, 0);
        }
      });
      
      setPositions(newPositions);
    };
    
    calculatePositions();
  }, [suggestions, blockElement, isMobile]);
  
  // Trier les suggestions par date
  const sortedSuggestions = useMemo(() => {
    return [...suggestions].sort((a, b) => 
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
  }, [suggestions]);
  
  // Version mobile - reste identique
  if (isMobile) {
    return (
      <div style={styles.mobileContainer}>
        {sortedSuggestions.map(suggestion => (
          <div key={suggestion._id} style={styles.card}>
            {suggestion.position?.selectedText && (
              <div style={styles.selectedText}>
                "{suggestion.position.selectedText}"
              </div>
            )}
            <div style={styles.date}>
              {new Date(suggestion.createdAt).toLocaleDateString('fr-FR')}
            </div>
            <p style={styles.content}>{suggestion.content}</p>
            <div style={styles.buttons}>
              <button
                onClick={() => onAccept(suggestion)}
                style={styles.acceptButton}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#059669'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#10b981'}
              >
                
              </button>
              <button
                onClick={() => onReject(suggestion)}
                style={styles.rejectButton}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#dc2626'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#ef4444'}
              >
                
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  }
  
  // Version desktop - positionnement à gauche
  return (
    <div style={styles.container}>
      {sortedSuggestions.map((suggestion, index) => {
        const position = positions.get(suggestion._id) || 0;
        
        return (
          <div
            key={suggestion._id}
            style={{
              ...styles.card,
              ...(hoveredSuggestion === suggestion._id ? styles.cardHovered : {}),
              marginTop: index === 0 ? `${position}px` : '8px',
              marginRight: `${Math.min(index * 10, 30)}px`  // Changé de marginLeft à marginRight
            }}
            onMouseEnter={() => setHoveredSuggestion(suggestion._id)}
            onMouseLeave={() => setHoveredSuggestion(null)}
          >
            {suggestion.position?.selectedText && (
              <div style={styles.selectedText} title={suggestion.position.selectedText}>
                "{suggestion.position.selectedText}"
              </div>
            )}
            <div style={styles.date}>
              {new Date(suggestion.createdAt).toLocaleDateString('fr-FR')}
            </div>
            <p style={styles.content}>{suggestion.content}</p>
            <div style={styles.buttons}>
              <button
                onClick={() => onAccept(suggestion)}
                style={styles.acceptButton}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#059669'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#10b981'}
              >
                
              </button>
              <button
                onClick={() => onReject(suggestion)}
                style={styles.rejectButton}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#dc2626'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#ef4444'}
              >
                
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PositionedSuggestions;